// UI 공용 DOM 도우미와 스타일 상수. 디자인 기준 해상도는 1280x720이고 화면 구석마다 transform: scale로 맞춘다.
import { assetUrl } from '../pwa/assetMap';
export const C = {
  navy: '#1A2238',
  navyDeep: '#10162a',
  gold: '#F2C14E',
  crimson: '#FF2A3D',
  teal: '#4FB6D0',
  stone: '#E9D8B4',
} as const;

export const FONT = 'system-ui,"Malgun Gothic","Apple SD Gothic Neo",sans-serif';
export const FONT_NUM = 'Impact,"Arial Black","Malgun Gothic",sans-serif';

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, css: string, parent?: HTMLElement, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.style.cssText = css;
  if (text !== undefined) e.textContent = text;
  if (parent) parent.appendChild(e);
  return e;
}

/** 금테 버튼. primary면 금색 채움. */
export function button(parent: HTMLElement, label: string, onClick: () => void, primary = false, testId?: string): HTMLButtonElement {
  const b = el(
    'button',
    'pointer-events:auto;cursor:pointer;font:900 22px ' + FONT + ';padding:12px 38px;border-radius:12px;letter-spacing:1px;' +
      'border:2px solid ' + C.gold + ';' +
      (primary
        ? 'background:linear-gradient(#ffd877,#e0a52f);color:#2a1d05;'
        : 'background:linear-gradient(#26335a,#1A2238);color:' + C.gold + ';') +
      'box-shadow:0 4px 0 rgba(0,0,0,.45);',
    parent,
    label,
  );
  if (testId) b.dataset.testid = testId;
  b.addEventListener('click', onClick);
  return b;
}

export function fmtTime(ticks: number): string {
  const s = Math.floor(ticks / 60);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return (m < 10 ? '0' : '') + m + ':' + (r < 10 ? '0' : '') + r;
}

export function fmtNum(n: number): string {
  const v = Math.round(n);
  const s = String(Math.abs(v));
  let out = '';
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) out += ',';
    out += s[i];
  }
  return v < 0 ? '-' + out : out;
}

/** 디자인 해상도 기준 배율(최소 0.55) */
export function designScale(w: number, h: number): number {
  return Math.max(0.55, Math.min(w / 1280, h / 720));
}

// ── HUD 이미지 에셋(public/assets/ui/hud/*.webp, GPT Image로 만든 콘셉트 스타일 원화) ──────────
/** 스킬 아이콘·초상(정사각, 원형으로 잘라 쓴다)과 투명 틀·패널 */
export const HUD_IMAGES = [
  'attack', 'dash', 'storm', 'fall', 'crescent', 'dodge', 'awaken', 'portrait', 'portrait_t2', 'portrait_t3',
  'ringSkill', 'ringAttack', 'ringAwaken', 'portraitFrame', 'minimapRing', 'barFrame', 'panelObjective', 'panelStage',
] as const;
export type HudImage = (typeof HUD_IMAGES)[number];
/** 장착 갑옷 단계(T1~T3)의 카엘 초상. 3D 외형과 같은 차림(qa: work/cycles/20261018-gear-tiers/art) */
export function portraitImage(tier: 1 | 2 | 3): HudImage {
  return tier === 3 ? 'portrait_t3' : tier === 2 ? 'portrait_t2' : 'portrait';
}

/** QA: ?hudfail=1 이면 모든 HUD 이미지를 일부러 실패시켜 CSS 대체 모양을 확인한다 */
const HUD_FAIL = typeof location !== 'undefined' && /[?&]hudfail=1/.test(location.search);
export function hudUrl(name: HudImage): string {
  return assetUrl('ui/hud/' + (HUD_FAIL ? 'missing-' : '') + name + '.webp');
}

let preloaded = false;
/** 첫 화면 전에 HUD 이미지를 모두 받아 둔다(같은 URL의 <img>는 캐시에서 바로 그린다). 여러 번 불러도 한 번만 한다 */
export function preloadHud(): void {
  if (preloaded || typeof Image === 'undefined') return;
  preloaded = true;
  for (const n of HUD_IMAGES) {
    const im = new Image();
    im.decoding = 'async';
    im.src = hudUrl(n);
  }
}

/**
 * HUD 이미지 <img>. 받기에 실패하면 이미지를 숨기고 onFail로 CSS 대체 모양을 켠다.
 * 이벤트는 생성 시 한 번만 붙인다(프레임 루프와 무관).
 */
export function hudImg(name: HudImage, css: string, parent: HTMLElement, onFail?: () => void): HTMLImageElement {
  const im = el('img', 'display:block;pointer-events:none;user-select:none;-webkit-user-drag:none;' + css, parent);
  im.alt = '';
  im.draggable = false;
  im.decoding = 'async';
  im.addEventListener('error', () => {
    im.style.display = 'none';
    onFail?.();
  }, { once: true });
  im.src = hudUrl(name);
  return im;
}

/** 금색 그라데이션 글자(콘셉트의 금빛 숫자·제목). 배경을 글자 모양으로 잘라 칠한다 */
export const GOLD_TEXT = 'background:linear-gradient(180deg,#fff6c8 0%,#ffd76a 45%,#e0a12f 70%,#a8641a 100%);-webkit-background-clip:text;background-clip:text;color:transparent;';
/** 글자 테두리 그림자(겹친 text-shadow로 어두운 외곽선) */
export const TEXT_OUTLINE = 'text-shadow:0 1px 0 #000,1px 0 0 #000,0 -1px 0 #000,-1px 0 0 #000,0 2px 4px rgba(0,0,0,.8);';
